import React, { useState, useCallback, useMemo } from 'react';
import {
  TextField,
  Button,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  IconButton,
  Checkbox,
  Typography,
  Chip,
  Divider,
  Fade,
  LinearProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HomeIcon from '@mui/icons-material/Home';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ShieldIcon from '@mui/icons-material/Shield';
import WaterIcon from '@mui/icons-material/Water';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import RateReviewIcon from '@mui/icons-material/RateReview';
import DescriptionIcon from '@mui/icons-material/Description';
import api from '../services/api';

// ═══════════════════════════════════════════════════════════════
// WIZARD STEP DEFINITIONS
// ═══════════════════════════════════════════════════════════════

const STEPS = [
  { key: 'identity',  label: 'Basic Identity',    icon: <PersonIcon />,      color: '#2563eb' },
  { key: 'address',   label: 'Address',           icon: <HomeIcon />,        color: '#d97706' },
  { key: 'farm',      label: 'Farm & Pond',       icon: <AgricultureIcon />, color: '#059669' },
  { key: 'documents', label: 'Document Upload',   icon: <DescriptionIcon />, color: '#7c3aed' },
  { key: 'review',    label: 'Review & Submit',   icon: <RateReviewIcon />,  color: '#0891b2' },
];

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const PublicOnboardingForm = () => {
  const [activeStep, setActiveStep] = useState(0);

  // ── Form state (unchanged from original) ──
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    aadhar_number: '',
    pan_number: '',
    kyc_address: '',
    farm_address: '',
    // Structured residential address
    house_no: '',
    village: '',
    gram_panchayat: '',
    block: '',
    district: '',
    state: '',
    pincode: '',
    // Structured farm address
    same_as_residential: false,
    farm_village: '',
    farm_block: '',
    farm_district: '',
    farm_state: '',
    farm_pincode: '',
    // Farm details
    pond_size: '',
    interested_in_credit: 'no',
  });

  const [files, setFiles] = useState({
    aadhar_front: null,
    aadhar_back: null,
    pan_photo: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [responseData, setResponseData] = useState(null);

  // ── Handlers (unchanged) ──
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setForm((prev) => {
      const updated = { ...prev, [name]: newVal };
      if (name === 'same_as_residential' && checked) {
        updated.farm_village = prev.village;
        updated.farm_block = prev.block;
        updated.farm_district = prev.district;
        updated.farm_state = prev.state;
        updated.farm_pincode = prev.pincode;
        if (prev.kyc_address && !updated.farm_address) {
          updated.farm_address = prev.kyc_address;
        }
      }
      return updated;
    });
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError(null);
  }, []);

  const handleFileChange = useCallback((fieldName) => (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];
      if (!allowed.includes(file.type)) {
        setErrors((prev) => ({ ...prev, [fieldName]: 'Only JPEG, PNG, WebP, or PDF files allowed.' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, [fieldName]: 'File must be less than 5 MB.' }));
        return;
      }
    }
    setFiles((prev) => ({ ...prev, [fieldName]: file }));
    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
  }, []);

  const removeFile = useCallback((fieldName) => () => {
    setFiles((prev) => ({ ...prev, [fieldName]: null }));
  }, []);

  // ── Per-step validation ──
  const validateStep = useCallback((step) => {
    const errs = {};

    if (step === 0) {
      // Basic Identity
      if (!form.name.trim()) errs.name = 'Full name is required.';
      if (!form.mobile.trim()) errs.mobile = 'Mobile number is required.';
      else if (!/^\d{10}$/.test(form.mobile)) errs.mobile = 'Must be exactly 10 digits.';
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errs.email = 'Enter a valid email address.';
      }
      if (!form.aadhar_number.trim()) errs.aadhar_number = 'Aadhar number is required.';
      else if (!/^\d{12}$/.test(form.aadhar_number)) errs.aadhar_number = 'Must be exactly 12 digits.';
      if (!form.pan_number.trim()) errs.pan_number = 'PAN number is required.';
      else if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.toUpperCase())) {
        errs.pan_number = 'Invalid PAN format (e.g. ABCDE1234F).';
      }
    }

    if (step === 1) {
      // Address
      if (!form.village.trim()) errs.village = 'Village is required.';
      if (!form.block.trim()) errs.block = 'Block is required.';
      if (!form.district.trim()) errs.district = 'District is required.';
      if (!form.state.trim()) errs.state = 'State is required.';
      if (!form.pincode.trim()) errs.pincode = 'Pincode is required.';
      else if (!/^\d{6}$/.test(form.pincode.trim())) errs.pincode = 'Pincode must be 6 digits.';
      if (!form.same_as_residential) {
        if (!form.farm_district.trim()) errs.farm_district = 'Farm district is required.';
        if (!form.farm_pincode.trim()) errs.farm_pincode = 'Farm pincode is required.';
        else if (!/^\d{6}$/.test(form.farm_pincode.trim())) errs.farm_pincode = 'Pincode must be 6 digits.';
      }
    }

    if (step === 2) {
      // Farm & Pond
      if (!form.pond_size || parseFloat(form.pond_size) <= 0) errs.pond_size = 'Pond size is required and must be greater than 0.';
    }

    if (step === 3) {
      // Documents
      if (!files.aadhar_front) errs.aadhar_front = 'Aadhar front photo is required.';
      if (!files.aadhar_back) errs.aadhar_back = 'Aadhar back photo is required.';
      if (!files.pan_photo) errs.pan_photo = 'PAN photo is required.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form, files]);

  // ── Navigation ──
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const goToStep = (step) => {
    // Allow going back freely; going forward requires validation of all prior steps
    if (step < activeStep) {
      setActiveStep(step);
    } else {
      // Validate current step first
      for (let i = activeStep; i < step; i++) {
        if (!validateStep(i)) return;
      }
      setActiveStep(step);
    }
  };

  // ── Submit (unchanged payload) ──
  const handleSubmit = async () => {
    // Validate all steps
    for (let i = 0; i < STEPS.length - 1; i++) {
      if (!validateStep(i)) {
        setActiveStep(i);
        return;
      }
    }

    setLoading(true);
    setApiError(null);

    try {
      const residentialParts = [form.house_no, form.village, form.gram_panchayat, form.block, form.district, form.state, form.pincode]
        .map(s => (s || '').trim()).filter(Boolean);
      const kyc_address_built = form.kyc_address.trim() || residentialParts.join(', ');

      const farmParts = form.same_as_residential
        ? residentialParts
        : [form.farm_village, form.farm_block, form.farm_district, form.farm_state, form.farm_pincode]
            .map(s => (s || '').trim()).filter(Boolean);
      const farm_address_built = form.farm_address.trim() || farmParts.join(', ');

      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('mobile', form.mobile.trim());
      if (form.email) formData.append('email', form.email.trim());
      formData.append('aadhar_number', form.aadhar_number.trim());
      formData.append('pan_number', form.pan_number.trim().toUpperCase());
      formData.append('kyc_address', kyc_address_built);
      formData.append('farm_address', farm_address_built);
      formData.append('pond_size', parseFloat(form.pond_size));
      formData.append('interested_in_credit', form.interested_in_credit === 'yes');
      formData.append('house_no', (form.house_no || '').trim());
      formData.append('village', (form.village || '').trim());
      formData.append('gram_panchayat', (form.gram_panchayat || '').trim());
      formData.append('block', (form.block || '').trim());
      formData.append('district', (form.district || '').trim());
      formData.append('state', (form.state || '').trim());
      formData.append('pincode', (form.pincode || '').trim());
      formData.append('same_as_residential', form.same_as_residential ? 'true' : 'false');
      formData.append('farm_village', form.same_as_residential ? (form.village || '').trim() : (form.farm_village || '').trim());
      formData.append('farm_block', form.same_as_residential ? (form.block || '').trim() : (form.farm_block || '').trim());
      formData.append('farm_district', form.same_as_residential ? (form.district || '').trim() : (form.farm_district || '').trim());
      formData.append('farm_state', form.same_as_residential ? (form.state || '').trim() : (form.farm_state || '').trim());
      formData.append('farm_pincode', form.same_as_residential ? (form.pincode || '').trim() : (form.farm_pincode || '').trim());
      formData.append('aadhar_front', files.aadhar_front);
      formData.append('aadhar_back', files.aadhar_back);
      formData.append('pan_photo', files.pan_photo);

      const response = await api.post('/api/v1/public/register-farmer', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResponseData(response.data);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setApiError(detail);
      } else if (Array.isArray(detail)) {
        setApiError(detail.map((d) => d.msg || d.message || JSON.stringify(d)).join('. '));
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Progress percentage ──
  const progressPercent = useMemo(() => ((activeStep + 1) / STEPS.length) * 100, [activeStep]);

  // ── Step completeness for stepper indicators ──
  const stepComplete = useMemo(() => {
    const check = (step) => {
      if (step === 0) return !!(form.name && form.mobile && form.aadhar_number && form.pan_number);
      if (step === 1) return !!(form.village && form.block && form.district && form.state && form.pincode);
      if (step === 2) return !!(form.pond_size && parseFloat(form.pond_size) > 0);
      if (step === 3) return !!(files.aadhar_front && files.aadhar_back && files.pan_photo);
      return false;
    };
    return STEPS.map((_, i) => check(i));
  }, [form, files]);

  // ═══════════════════════════════════════════════════════════════
  // SUCCESS SCREEN
  // ═══════════════════════════════════════════════════════════════

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white shadow-xl rounded-2xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircleIcon sx={{ fontSize: 48, color: '#16a34a' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Application Submitted Successfully
          </h1>
          <p className="text-gray-500 mb-6 leading-relaxed">
            Our team will verify your KYC within <strong>24-48 hours</strong>.
            <br />
            You will receive login details via <strong>SMS</strong> once approved.
          </p>
          {responseData?.farmer_id && (
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <p className="text-xs text-blue-400 mb-1">Application Reference</p>
              <p className="text-lg font-bold text-blue-700">{responseData.farmer_id}</p>
            </div>
          )}
          <div className="space-y-2 text-left bg-gray-50 rounded-xl p-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Name</span>
              <span className="text-gray-700 font-medium">{responseData?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Mobile</span>
              <span className="text-gray-700 font-medium">{responseData?.mobile}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Status</span>
              <span className="text-amber-600 font-semibold">{responseData?.status || 'PENDING_KYC'}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-6">
            Questions? Contact our support at <strong>1800-XXX-XXXX</strong>
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // WIZARD FORM
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {/* ── Header ── */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
            <AgricultureIcon sx={{ fontSize: 32, color: '#fff' }} />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Apply for Credit Cultivation &amp; Insurance
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Complete all steps below. Our team will verify and activate your account.
          </p>
        </div>

        {/* ── Progress Bar ── */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1.5">
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: STEPS[activeStep].color }}>
              {Math.round(progressPercent)}% Complete
            </Typography>
          </div>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: STEPS[activeStep].color,
                transition: 'transform 0.4s ease, background-color 0.3s ease',
              },
            }}
          />
        </div>

        {/* ── Step Indicators ── */}
        <div className="flex items-center justify-between mb-6 px-1">
          {STEPS.map((step, idx) => {
            const isActive = idx === activeStep;
            const isDone = stepComplete[idx] && idx < activeStep;
            const isPast = idx < activeStep;
            return (
              <button
                key={step.key}
                onClick={() => goToStep(idx)}
                className="flex flex-col items-center gap-1 group outline-none"
                style={{ flex: 1 }}
                type="button"
              >
                <div
                  className="flex items-center justify-center rounded-full transition-all duration-300"
                  style={{
                    width: isActive ? 44 : 36,
                    height: isActive ? 44 : 36,
                    backgroundColor: isDone ? '#dcfce7' : isActive ? step.color : isPast ? `${step.color}18` : '#f1f5f9',
                    border: isActive ? `2.5px solid ${step.color}` : isDone ? '2px solid #22c55e' : '2px solid transparent',
                    boxShadow: isActive ? `0 4px 14px ${step.color}30` : 'none',
                  }}
                >
                  {isDone ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: '#16a34a' }} />
                  ) : (
                    React.cloneElement(step.icon, {
                      sx: {
                        fontSize: isActive ? 20 : 17,
                        color: isActive ? step.color : isPast ? step.color : '#94a3b8',
                      },
                    })
                  )}
                </div>
                <span
                  className="text-center leading-tight transition-colors duration-200"
                  style={{
                    fontSize: isActive ? '0.68rem' : '0.6rem',
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? step.color : isDone ? '#16a34a' : isPast ? '#64748b' : '#94a3b8',
                    maxWidth: 72,
                  }}
                >
                  {step.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Form Card ── */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Step title bar */}
          <div
            className="px-6 py-3 flex items-center gap-2"
            style={{ background: `linear-gradient(135deg, ${STEPS[activeStep].color}12 0%, ${STEPS[activeStep].color}05 100%)`, borderBottom: `2px solid ${STEPS[activeStep].color}20` }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${STEPS[activeStep].color}15` }}
            >
              {React.cloneElement(STEPS[activeStep].icon, { sx: { fontSize: 18, color: STEPS[activeStep].color } })}
            </div>
            <div>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                {STEPS[activeStep].label}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.2 }}>
                {activeStep === 0 && 'Enter your name, contact, and identity details'}
                {activeStep === 1 && 'Provide your residential and farm address'}
                {activeStep === 2 && 'Tell us about your farm and pond'}
                {activeStep === 3 && 'Upload your KYC documents'}
                {activeStep === 4 && 'Review all information before submitting'}
              </Typography>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {apiError && (
              <Alert severity="error" onClose={() => setApiError(null)} sx={{ mb: 3, borderRadius: 2 }}>
                {apiError}
              </Alert>
            )}

            {/* ══════════ STEP 1: Basic Identity ══════════ */}
            <Fade in={activeStep === 0} mountOnEnter unmountOnExit>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    name="name" label="Full Name *" value={form.name} onChange={handleChange}
                    error={!!errors.name} helperText={errors.name} fullWidth size="small"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    name="mobile" label="Mobile Number *" value={form.mobile} onChange={handleChange}
                    error={!!errors.mobile} helperText={errors.mobile} fullWidth size="small"
                    inputProps={{ maxLength: 10 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    name="email" label="Email (Optional)" value={form.email} onChange={handleChange}
                    error={!!errors.email} helperText={errors.email} fullWidth size="small"
                    className="sm:col-span-2"
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                  />
                </div>

                <Divider sx={{ my: 1 }}>
                  <Chip label="Identity Verification" size="small" icon={<VerifiedUserIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b' }} />
                </Divider>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    name="aadhar_number" label="Aadhar Number *" value={form.aadhar_number} onChange={handleChange}
                    error={!!errors.aadhar_number} helperText={errors.aadhar_number} fullWidth size="small"
                    inputProps={{ maxLength: 12 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    name="pan_number" label="PAN Number *" value={form.pan_number}
                    onChange={(e) => handleChange({ target: { name: 'pan_number', value: e.target.value.toUpperCase() } })}
                    error={!!errors.pan_number} helperText={errors.pan_number} fullWidth size="small"
                    inputProps={{ maxLength: 10 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                  />
                </div>
              </div>
            </Fade>

            {/* ══════════ STEP 2: Address ══════════ */}
            <Fade in={activeStep === 1} mountOnEnter unmountOnExit>
              <div className="space-y-5">
                {/* Residential */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <HomeIcon sx={{ fontSize: 18, color: '#d97706' }} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>Residential Address</Typography>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextField name="house_no" label="House / Door No." value={form.house_no} onChange={handleChange}
                      error={!!errors.house_no} helperText={errors.house_no} fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start"><HomeIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <TextField name="village" label="Village *" value={form.village} onChange={handleChange}
                      error={!!errors.village} helperText={errors.village} required fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <TextField name="gram_panchayat" label="Gram Panchayat" value={form.gram_panchayat} onChange={handleChange}
                      error={!!errors.gram_panchayat} helperText={errors.gram_panchayat} fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <TextField name="block" label="Block *" value={form.block} onChange={handleChange}
                      error={!!errors.block} helperText={errors.block} required fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <TextField name="district" label="District *" value={form.district} onChange={handleChange}
                      error={!!errors.district} helperText={errors.district} required fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <TextField name="state" label="State *" value={form.state} onChange={handleChange}
                      error={!!errors.state} helperText={errors.state} required fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <TextField name="pincode" label="Pincode *" value={form.pincode} onChange={handleChange}
                      error={!!errors.pincode} helperText={errors.pincode} required fullWidth size="small"
                      inputProps={{ maxLength: 6 }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                    />
                  </div>
                </div>

                <Divider />

                {/* Farm Address */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AgricultureIcon sx={{ fontSize: 18, color: '#059669' }} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>Farm Address</Typography>
                  </div>
                  <FormControlLabel
                    control={<Checkbox name="same_as_residential" checked={form.same_as_residential} onChange={handleChange} size="small" />}
                    label={<span className="text-sm text-gray-600">Same as residential address</span>}
                    sx={{ mb: 1 }}
                  />
                  {!form.same_as_residential && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                      <TextField name="farm_village" label="Farm Village" value={form.farm_village} onChange={handleChange}
                        error={!!errors.farm_village} helperText={errors.farm_village} fullWidth size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><AgricultureIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                      />
                      <TextField name="farm_block" label="Farm Block" value={form.farm_block} onChange={handleChange}
                        error={!!errors.farm_block} helperText={errors.farm_block} fullWidth size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                      />
                      <TextField name="farm_district" label="Farm District *" value={form.farm_district} onChange={handleChange}
                        error={!!errors.farm_district} helperText={errors.farm_district} required fullWidth size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                      />
                      <TextField name="farm_state" label="Farm State" value={form.farm_state} onChange={handleChange}
                        error={!!errors.farm_state} helperText={errors.farm_state} fullWidth size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                      />
                      <TextField name="farm_pincode" label="Farm Pincode *" value={form.farm_pincode} onChange={handleChange}
                        error={!!errors.farm_pincode} helperText={errors.farm_pincode} required fullWidth size="small"
                        inputProps={{ maxLength: 6 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                      />
                    </div>
                  )}
                  {form.same_as_residential && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-2 flex items-center gap-2">
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: 600 }}>
                        Farm address will use your residential address
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </Fade>

            {/* ══════════ STEP 3: Farm & Pond ══════════ */}
            <Fade in={activeStep === 2} mountOnEnter unmountOnExit>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    name="pond_size" label="Pond Size (hectares) *" type="number" value={form.pond_size}
                    onChange={handleChange} error={!!errors.pond_size} helperText={errors.pond_size}
                    required fullWidth size="small" inputProps={{ min: 0.1, step: 0.1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><WaterIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment> }}
                  />
                  <div>
                    <FormLabel component="legend" sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 0.5 }}>
                      Interested in Credit?
                    </FormLabel>
                    <RadioGroup row name="interested_in_credit" value={form.interested_in_credit} onChange={handleChange}>
                      <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                  </div>
                </div>

                {/* Info card */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <WaterIcon sx={{ fontSize: 24, color: '#2563eb', mt: 0.3 }} />
                    <div>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                        Why do we need pond size?
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.5 }}>
                        Pond size helps us calculate your insurance premium, credit eligibility, and provide
                        tailored recommendations for your aquaculture operations. Enter the total water spread area in hectares.
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </Fade>

            {/* ══════════ STEP 4: Document Upload ══════════ */}
            <Fade in={activeStep === 3} mountOnEnter unmountOnExit>
              <div className="space-y-5">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2 flex items-center gap-2">
                  <ShieldIcon sx={{ fontSize: 18, color: '#d97706' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>
                    Upload clear photos/scans of your documents. Accepted: JPEG, PNG, WebP, PDF (max 5MB each).
                  </Typography>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FileUploadBox
                    label="Aadhar Front *" file={files.aadhar_front} error={errors.aadhar_front}
                    onChange={handleFileChange('aadhar_front')} onRemove={removeFile('aadhar_front')} inputId="aadhar-front-input"
                  />
                  <FileUploadBox
                    label="Aadhar Back *" file={files.aadhar_back} error={errors.aadhar_back}
                    onChange={handleFileChange('aadhar_back')} onRemove={removeFile('aadhar_back')} inputId="aadhar-back-input"
                  />
                  <FileUploadBox
                    label="PAN Photo *" file={files.pan_photo} error={errors.pan_photo}
                    onChange={handleFileChange('pan_photo')} onRemove={removeFile('pan_photo')} inputId="pan-photo-input"
                  />
                </div>

                {/* Status indicators */}
                <div className="flex items-center justify-center gap-6 mt-4">
                  {[
                    { label: 'Aadhar Front', ok: !!files.aadhar_front },
                    { label: 'Aadhar Back', ok: !!files.aadhar_back },
                    { label: 'PAN Photo', ok: !!files.pan_photo },
                  ].map((doc) => (
                    <div key={doc.label} className="flex items-center gap-1.5">
                      {doc.ok ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                      <span className={`text-xs font-medium ${doc.ok ? 'text-green-700' : 'text-gray-400'}`}>
                        {doc.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Fade>

            {/* ══════════ STEP 5: Review & Submit ══════════ */}
            <Fade in={activeStep === 4} mountOnEnter unmountOnExit>
              <div className="space-y-4">
                {/* Identity Review */}
                <ReviewSection
                  title="Basic Identity"
                  icon={<PersonIcon sx={{ fontSize: 16, color: '#2563eb' }} />}
                  color="#2563eb"
                  onEdit={() => setActiveStep(0)}
                  items={[
                    { label: 'Full Name', value: form.name },
                    { label: 'Mobile', value: form.mobile },
                    { label: 'Email', value: form.email || '—' },
                    { label: 'Aadhar', value: form.aadhar_number ? `XXXX-XXXX-${form.aadhar_number.slice(-4)}` : '—' },
                    { label: 'PAN', value: form.pan_number || '—' },
                  ]}
                />

                {/* Address Review */}
                <ReviewSection
                  title="Address"
                  icon={<HomeIcon sx={{ fontSize: 16, color: '#d97706' }} />}
                  color="#d97706"
                  onEdit={() => setActiveStep(1)}
                  items={[
                    { label: 'Residential', value: [form.house_no, form.village, form.gram_panchayat, form.block, form.district, form.state, form.pincode].filter(Boolean).join(', ') || '—' },
                    { label: 'Farm Address', value: form.same_as_residential ? 'Same as residential' : [form.farm_village, form.farm_block, form.farm_district, form.farm_state, form.farm_pincode].filter(Boolean).join(', ') || '—' },
                  ]}
                />

                {/* Farm Review */}
                <ReviewSection
                  title="Farm & Pond"
                  icon={<AgricultureIcon sx={{ fontSize: 16, color: '#059669' }} />}
                  color="#059669"
                  onEdit={() => setActiveStep(2)}
                  items={[
                    { label: 'Pond Size', value: form.pond_size ? `${form.pond_size} hectares` : '—' },
                    { label: 'Credit Interest', value: form.interested_in_credit === 'yes' ? 'Yes' : 'No' },
                  ]}
                />

                {/* Documents Review */}
                <ReviewSection
                  title="Documents"
                  icon={<DescriptionIcon sx={{ fontSize: 16, color: '#7c3aed' }} />}
                  color="#7c3aed"
                  onEdit={() => setActiveStep(3)}
                  items={[
                    { label: 'Aadhar Front', value: files.aadhar_front ? `${files.aadhar_front.name} (${(files.aadhar_front.size / 1024).toFixed(0)} KB)` : 'Not uploaded' },
                    { label: 'Aadhar Back', value: files.aadhar_back ? `${files.aadhar_back.name} (${(files.aadhar_back.size / 1024).toFixed(0)} KB)` : 'Not uploaded' },
                    { label: 'PAN Photo', value: files.pan_photo ? `${files.pan_photo.name} (${(files.pan_photo.size / 1024).toFixed(0)} KB)` : 'Not uploaded' },
                  ]}
                />

                {/* Confirmation */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3 mt-2">
                  <CheckCircleIcon sx={{ fontSize: 22, color: '#16a34a', mt: 0.2 }} />
                  <div>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#166534' }}>
                      Ready to Submit
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#15803d', lineHeight: 1.5, mt: 0.3 }}>
                      Please review all information above. Click "Edit" on any section to make changes.
                      Once submitted, our team will verify your KYC within 24-48 hours.
                    </Typography>
                  </div>
                </div>
              </div>
            </Fade>

            {/* ══════════ NAVIGATION BUTTONS ══════════ */}
            <div className="flex items-center justify-between mt-8 pt-4" style={{ borderTop: '1px solid #e2e8f0' }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: activeStep === 0 ? '#cbd5e1' : '#64748b',
                  borderRadius: 2,
                  px: 3,
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                Back
              </Button>

              <div className="flex items-center gap-1.5">
                {STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: idx === activeStep ? 20 : 6,
                      height: 6,
                      backgroundColor: idx === activeStep ? STEPS[activeStep].color : idx < activeStep ? '#22c55e' : '#e2e8f0',
                    }}
                  />
                ))}
              </div>

              {activeStep < STEPS.length - 1 ? (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    bgcolor: STEPS[activeStep].color,
                    '&:hover': { bgcolor: STEPS[activeStep].color, filter: 'brightness(0.9)' },
                    boxShadow: `0 4px 14px ${STEPS[activeStep].color}30`,
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 4,
                    py: 1,
                    bgcolor: '#16a34a',
                    '&:hover': { bgcolor: '#15803d' },
                    boxShadow: '0 4px 14px rgba(22, 163, 74, 0.3)',
                  }}
                >
                  {loading ? 'Submitting…' : 'Submit Application'}
                </Button>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center gap-2 justify-center pt-4">
              <ShieldIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
              <p className="text-xs text-gray-400">
                Your data is secure and encrypted. We comply with all data protection regulations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

/** Review section card for Step 5 */
const ReviewSection = ({ title, icon, color, onEdit, items }) => (
  <div className="border rounded-xl overflow-hidden" style={{ borderColor: `${color}30` }}>
    <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: `${color}08` }}>
      <div className="flex items-center gap-2">
        {icon}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color }}>{title}</Typography>
      </div>
      <Button
        size="small"
        onClick={onEdit}
        sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, color, minWidth: 0, px: 1.5, borderRadius: 1.5 }}
      >
        Edit
      </Button>
    </div>
    <div className="px-4 py-2.5 space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start justify-between gap-4">
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, minWidth: 90, flexShrink: 0 }}>
            {item.label}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#1e293b', fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>
            {item.value}
          </Typography>
        </div>
      ))}
    </div>
  </div>
);

/** File upload box with preview */
const FileUploadBox = ({ label, file, error, onChange, onRemove, inputId }) => (
  <div>
    <input
      id={inputId}
      type="file"
      accept="image/jpeg,image/png,image/webp,application/pdf"
      onChange={onChange}
      className="hidden"
    />
    {file ? (
      <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4 text-center">
        <InsertDriveFileIcon sx={{ fontSize: 32, color: '#16a34a' }} />
        <p className="text-xs text-green-700 font-medium mt-1 truncate">{file.name}</p>
        <p className="text-[10px] text-green-500">{(file.size / 1024).toFixed(0)} KB</p>
        <IconButton size="small" onClick={onRemove} sx={{ mt: 0.5 }}>
          <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
        </IconButton>
      </div>
    ) : (
      <label
        htmlFor={inputId}
        className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-5 cursor-pointer transition-colors duration-200 ${
          error
            ? 'border-red-300 bg-red-50 hover:border-red-400'
            : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
        }`}
      >
        <CloudUploadIcon sx={{ fontSize: 32, color: error ? '#ef4444' : '#9ca3af' }} />
        <span className={`text-xs mt-1.5 font-medium ${error ? 'text-red-500' : 'text-gray-500'}`}>
          {label}
        </span>
        <span className="text-[10px] text-gray-400 mt-0.5">JPEG, PNG, PDF &bull; Max 5MB</span>
      </label>
    )}
    {error && !file && (
      <p className="text-xs text-red-500 mt-1">{error}</p>
    )}
  </div>
);

export default PublicOnboardingForm;
